import { of, fromEventPattern } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  pluck,
  switchMap,
} from "rxjs/operators";
import { combineEpics, Epic } from "redux-observable";
import { database } from "firebase/app";

import { IRootState } from "../../rootReducer";

import {
  ROOT_FOLDER,
  REQUEST_LIBRARY,
  REQUEST_LIBRARY_SUCCESS,
  SUBSCRIBE_TO_FOLDER,
  SUBSCRIBE_TO_RECORD,
} from "./constants";
import {
  subscribeToFolderSuccess,
  subscribeToFolderError,
  subscribeToRecordSuccess,
  subscribeToRecordError,
} from "./actions";
import {
  LibraryActions,
  RequestLibrarySuccess,
  SubscribeToFolderError,
  SubscribeToFolderSuccess,
  SubscribeToRecordError,
  SubscribeToRecordSuccess,
} from "./actionTypes";
import {
  getActiveFolderId,
  getActiveRecord,
} from "./selectors";

import { getUser } from "../session/selectors";

type RequestLibraryInfoEpic = Epic<LibraryActions, RequestLibrarySuccess, IRootState>;
const requestLibraryInfoEpic: RequestLibraryInfoEpic = (action$, state$) => (
  action$.ofType(REQUEST_LIBRARY).pipe(
    switchMap(() => (
      state$.pipe(
        map(getUser),
        filter(Boolean),
        pluck('uid'),
        distinctUntilChanged(),
      )
    )),
    switchMap((uid) => (
      database()
        .ref(`libraries/${uid}`)
        .once("value")
    )),
    map((snapshot) => ({
      type: REQUEST_LIBRARY_SUCCESS,
      payload: snapshot.val() || {
        dateAdded: Math.round(Date.now() / 1000),
        isWasOpened: true,
        lastOpenDate: Math.round(Date.now() / 1000),
        title: "Library",
      }
    }))
  )
);

type SubscribeToFolderEpic = Epic<LibraryActions, SubscribeToFolderSuccess | SubscribeToFolderError, IRootState>
const subscribeToFolderContentEpic: SubscribeToFolderEpic = (action$, state$) => (
  action$.ofType(SUBSCRIBE_TO_FOLDER).pipe(
    switchMap(() => (
      state$.pipe(
        map(getActiveFolderId),
        filter(Boolean),
        distinctUntilChanged(),
      )
    )),
    switchMap(activeFolderId => (
      (
        activeFolderId === ROOT_FOLDER
          ? state$.pipe(
            map(getUser),
            filter(Boolean),
            pluck('uid'),
            distinctUntilChanged(),
          )
          : of(activeFolderId)
      ).pipe(
        switchMap((folderId) => {
          const folderRef = database().ref(`folders/${folderId}`);

          return fromEventPattern<database.DataSnapshot>(
            handler => folderRef.on("value", handler),
            (handler) => folderRef.off("value", handler)
          );
        }),
        map(snapshot => (
          snapshot.exists()
            ? subscribeToFolderSuccess(activeFolderId as string, snapshot.val())
            : subscribeToFolderError(activeFolderId as string)
        )),
      )
    )),
  )
);

type SubscribeToRecordContentEpic = Epic<LibraryActions, SubscribeToRecordSuccess | SubscribeToRecordError, IRootState>;
const subscribeToRecordContentEpic: SubscribeToRecordContentEpic = (action$, state$) => (
  action$.ofType(SUBSCRIBE_TO_RECORD).pipe(
    switchMap(() => (
      state$.pipe(
        map(getActiveRecord),
        filter(Boolean),
        distinctUntilChanged(),
      )
    )),
    switchMap(activeRecordId =>
      of(activeRecordId).pipe(
        switchMap((recordId) => {
          const recordRef = database().ref(`books/${recordId}`);

          return fromEventPattern<database.DataSnapshot>(
            (handler) => recordRef.on("value", handler),
            (handler) => recordRef.off("value", handler)
          )
        }),
        map(snapshot => (
          snapshot.exists()
            ? subscribeToRecordSuccess(activeRecordId as string, snapshot.val())
            : subscribeToRecordError(activeRecordId as string)
        )),
      )
    ),
  )
);

export default combineEpics(
  requestLibraryInfoEpic,
  subscribeToFolderContentEpic,
  subscribeToRecordContentEpic,
);
